import {
  languageMap,
  type LanguageKey,
  type MetaIntroProps,
} from "@op/puck-types";
import {
  Drawer,
  FormattedDate,
  transformDoiOrUrl,
  useContent,
  useRender,
  useTranslation,
} from "@op/utils";
import { useCallback, useMemo, useState } from "react";
import LayoutRow from "../../../../../components/puck/LayoutRow/LayoutRow";
import Medium from "../../../../../components/puck/Medium/Medium";
import { HeadingNode } from "../../../../../components/base/HeadingNode";
import {
  Accordion,
  Button,
  Dc_Button_Close,
  Dc_MetaIntro,
  Dc_MetaIntro_ActionStack,
  Dc_MetaIntro_DrawerContent,
  Dc_Popover_Card_CiteAs,
  Dc_Text_Citation,
  DropdownMenu,
  LinkButton,
} from "@op/sf-op";
import {
  FeatherArrowUpRight,
  FeatherChevronDown,
  DropdownMenu as SfCDropdownMenu,
  Popover as SfcPopover,
} from "@subframe/core";
import AuthorCard from "../../../../../components/puck/AuthorCard/AuthorCard";
import { ShareButton } from "../../../../../components/base/ShareButton";
import { CitationSourceLink } from "../../../../../components/base/CitationSourceLink";

function getVersionUrl(versionId: string) {
  const currentUrl =
    typeof window !== "undefined"
      ? new URL(window.location.href)
      : new URL("https://renderer.openpaper.de");
  const currentPathname = currentUrl.pathname;
  if (currentPathname.startsWith("/preview")) {
    currentUrl.pathname = `/preview/${versionId}`;
  } else {
    const [_, languageKey, topicSlug, pageSlug, _oldVersionId] =
      currentPathname.split("/");
    const newPathname = `${languageKey}/${topicSlug}/${pageSlug}/${versionId}`;
    currentUrl.pathname = newPathname;
  }
  return currentUrl.toString();
}

const MetaIntro = ({
  publicationDate,
  hero,
  title,
  authors = [],
  languageLinks = [],
  citation,
  doi,
  contact,
  copyrightNote,
  imageCredits = [],
  open,
  onOpenChange,
}: MetaIntroProps) => {
  const [citeActive, setCiteActive] = useState<boolean>(false);
  const [doiActive, setDoiActive] = useState<boolean>(false);

  const { metaIntro: trans } = useTranslation();

  const languagePages = useMemo(
    () => languageLinks.filter((lang) => !!lang.page).map((lang) => lang.page),
    [languageLinks],
  );

  const { context } = useRender();
  const isNotRenderer = useMemo(() => context !== "renderer", [context]);
  const { versions, meta, citations, pdfUrl } = useContent();

  const onDoiButtonClick = useCallback(() => {
    if (!doi) return;
    setDoiActive(true);
    navigator.clipboard.writeText(doi);
    setTimeout(() => {
      setDoiActive(false);
    }, 2000);
  }, [doi]);

  const onCiteButtonClick = useCallback(() => {
    if (!citation) return;
    setCiteActive(true);
    navigator.clipboard.writeText(citation);
    setTimeout(() => {
      setCiteActive(false);
    }, 1000);
  }, [citation]);

  const allCitations = useMemo(() => {
    return [
      ...citations.content,
      ...Object.values(citations.excursions).flatMap((excursion) => excursion),
    ].sort((a, b) => a.index - b.index);
  }, [citations]);

  return (
    <>
      <div className="flex w-full flex-col gap-4">
        {hero && (
          <LayoutRow variant="full-width">
            <Medium
              medium={{
                ...hero,
                metadata: {
                  ...hero.metadata,
                  altText: "",
                  isFigure: false,
                  isExpandable: false,
                  source: {
                    url: undefined,
                    title: undefined,
                  },
                },
              }}
            />
          </LayoutRow>
        )}
        <LayoutRow variant="narrow">
          <Dc_MetaIntro
            titleSlot={<HeadingNode level={1}>{title}</HeadingNode>}
            publishedText={trans.published}
            versionLinkSlot={
              <span className="text-caption-bold font-caption-bold text-brand-700">
                {meta.publishedAt || publicationDate ? (
                  <FormattedDate
                    date={
                      publicationDate
                        ? new Date(publicationDate)
                        : (meta.publishedAt ?? new Date())
                    }
                    type="date"
                  />
                ) : (
                  trans.notYetPublished
                )}
              </span>
            }
            translationLinkSlot={
              <Dc_MetaIntro.MoreInformationLink
                disabled={isNotRenderer}
                onClick={() => onOpenChange(true)}
              >
                {trans.moreInformation}
              </Dc_MetaIntro.MoreInformationLink>
            }
            actionSlot={
              <>
                {pdfUrl && (
                  <a href={pdfUrl} target="_blank" className="contents">
                    <Button size="large" disabled={isNotRenderer}>
                      {trans.openPdf}
                    </Button>
                  </a>
                )}
                {languagePages.length > 0 &&
                  (languagePages.length === 1 ? (
                    <a
                      href={
                        isNotRenderer
                          ? undefined
                          : `/${languagePages[0]!.language}/${languagePages[0]!.topicSlug}/${languagePages[0]!.pageSlug}`
                      }
                      className="contents"
                    >
                      <Button
                        size="large"
                        variant="brand-secondary"
                        disabled={isNotRenderer}
                      >
                        {languageMap[languagePages[0]!.language as LanguageKey]}
                      </Button>
                    </a>
                  ) : (
                    <SfCDropdownMenu.Root>
                      <SfCDropdownMenu.Trigger asChild={true}>
                        <Button
                          size="large"
                          variant="brand-secondary"
                          iconRight={<FeatherChevronDown />}
                          disabled={isNotRenderer}
                        >
                          {trans.otherLanguages}
                        </Button>
                      </SfCDropdownMenu.Trigger>
                      <SfCDropdownMenu.Content
                        side="bottom"
                        align="start"
                        sideOffset={4}
                        asChild={true}
                      >
                        <DropdownMenu>
                          {languagePages.map((page) => (
                            <a
                              href={
                                isNotRenderer
                                  ? undefined
                                  : `/${page.language}/${page.topicSlug}/${page.pageSlug}`
                              }
                              className="contents"
                            >
                              <DropdownMenu.DropdownItem
                                key={page.language}
                                icon={null}
                                disabled={isNotRenderer}
                              >
                                {languageMap[page.language as LanguageKey]}
                              </DropdownMenu.DropdownItem>
                            </a>
                          ))}
                        </DropdownMenu>
                      </SfCDropdownMenu.Content>
                    </SfCDropdownMenu.Root>
                  ))}
              </>
            }
            shareSlot={<ShareButton size="large" />}
          />
        </LayoutRow>
      </div>
      <Drawer open={open} onOpenChange={onOpenChange}>
        <Dc_MetaIntro_DrawerContent
          titleSlot={<HeadingNode level={1}>{title}</HeadingNode>}
          badgeSlot={
            <Dc_MetaIntro_DrawerContent.TypeBadge>
              {trans.pageType}
            </Dc_MetaIntro_DrawerContent.TypeBadge>
          }
          closeButtonSlot={
            <Dc_Button_Close onClick={() => onOpenChange(false)} />
          }
          publishedText={trans.published}
          versionLinkSlot={
            <span className="text-caption-bold font-caption-bold text-brand-700">
              {meta.publishedAt ? (
                <FormattedDate date={meta.publishedAt} type="datetime" />
              ) : (
                trans.notYetPublished
              )}
            </span>
          }
          translationLinkSlot={
            <>
              {versions.length > 0 && (
                <SfCDropdownMenu.Root>
                  <SfCDropdownMenu.Trigger asChild={true}>
                    <Button
                      size="medium"
                      variant="brand-secondary"
                      iconRight={<FeatherChevronDown />}
                      disabled={isNotRenderer}
                    >
                      {trans.olderVersions}
                    </Button>
                  </SfCDropdownMenu.Trigger>
                  <SfCDropdownMenu.Content
                    side="bottom"
                    align="start"
                    sideOffset={4}
                    asChild={true}
                  >
                    <DropdownMenu>
                      {versions.map((version) => (
                        <a
                          href={
                            isNotRenderer
                              ? undefined
                              : getVersionUrl(version.id)
                          }
                          className="contents"
                        >
                          <DropdownMenu.DropdownItem
                            key={version.id}
                            icon={null}
                            disabled={isNotRenderer}
                          >
                            {
                              <FormattedDate
                                date={new Date(version.publishedAt)}
                                type="datetime"
                              />
                            }
                          </DropdownMenu.DropdownItem>
                        </a>
                      ))}
                    </DropdownMenu>
                  </SfCDropdownMenu.Content>
                </SfCDropdownMenu.Root>
              )}
            </>
          }
          actionSlot={
            <>
              {pdfUrl && (
                <a href={pdfUrl} target="_blank" className="contents">
                  <Button size="large" disabled={isNotRenderer}>
                    {trans.openPdf}
                  </Button>
                </a>
              )}
              {languagePages.length > 0 &&
                (languagePages.length === 1 ? (
                  <a
                    href={
                      isNotRenderer
                        ? undefined
                        : `/${languagePages[0]!.language}/${languagePages[0]!.topicSlug}/${languagePages[0]!.pageSlug}`
                    }
                    className="contents"
                  >
                    <Button
                      size="large"
                      variant="brand-secondary"
                      disabled={isNotRenderer}
                    >
                      {languageMap[languagePages[0]!.language as LanguageKey]}
                    </Button>
                  </a>
                ) : (
                  <SfCDropdownMenu.Root>
                    <SfCDropdownMenu.Trigger asChild={true}>
                      <Button
                        size="large"
                        variant="brand-secondary"
                        iconRight={<FeatherChevronDown />}
                        disabled={isNotRenderer}
                      >
                        {trans.otherLanguages}
                      </Button>
                    </SfCDropdownMenu.Trigger>
                    <SfCDropdownMenu.Content
                      side="bottom"
                      align="start"
                      sideOffset={4}
                      asChild={true}
                    >
                      <DropdownMenu>
                        {languagePages.map((page) => (
                          <a
                            href={
                              isNotRenderer
                                ? undefined
                                : `/${page.language}/${page.topicSlug}/${page.pageSlug}`
                            }
                            className="contents"
                          >
                            <DropdownMenu.DropdownItem
                              key={page.language}
                              icon={null}
                              disabled={isNotRenderer}
                            >
                              {languageMap[page.language as LanguageKey]}
                            </DropdownMenu.DropdownItem>
                          </a>
                        ))}
                      </DropdownMenu>
                    </SfCDropdownMenu.Content>
                  </SfCDropdownMenu.Root>
                ))}
              {doi && (
                <SfcPopover.Root>
                  <SfcPopover.Trigger asChild>
                    <Dc_MetaIntro_ActionStack.DoiButton>
                      {trans.doi}
                    </Dc_MetaIntro_ActionStack.DoiButton>
                  </SfcPopover.Trigger>
                  <SfcPopover.Content side="top" sideOffset={12}>
                    <Dc_MetaIntro_ActionStack.DoiPopover
                      linkSlot={
                        <a
                          href={transformDoiOrUrl(doi)}
                          target="_blank"
                          className="contents"
                        >
                          <LinkButton
                            variant="neutral"
                            size="small"
                            iconRight={<FeatherArrowUpRight />}
                          >
                            {transformDoiOrUrl(doi)}
                          </LinkButton>
                        </a>
                      }
                      interactionSlot={
                        <Dc_Popover_Card_CiteAs.CopyInteraction
                          onClick={onDoiButtonClick}
                          pressed={doiActive}
                          text={trans.copyDoi}
                          copiedText={trans.copied}
                        />
                      }
                    />
                  </SfcPopover.Content>
                </SfcPopover.Root>
              )}
              {doi && citation && (
                <SfcPopover.Root>
                  <SfcPopover.Trigger asChild>
                    <Dc_MetaIntro_ActionStack.CiteButton>
                      {trans.cite}
                    </Dc_MetaIntro_ActionStack.CiteButton>
                  </SfcPopover.Trigger>
                  <SfcPopover.Content side="top" sideOffset={12}>
                    <Dc_MetaIntro_ActionStack.CitePopover>
                      <Dc_Popover_Card_CiteAs
                        doi={transformDoiOrUrl(doi)}
                        versionLinkSlot={null}
                        linkSlot={
                          <a
                            href={transformDoiOrUrl(doi)}
                            target="_blank"
                            className="contents"
                          >
                            <LinkButton
                              variant="neutral"
                              size="small"
                              iconRight={<FeatherArrowUpRight />}
                            >
                              {transformDoiOrUrl(doi)}
                            </LinkButton>
                          </a>
                        }
                        copySlot={
                          <Dc_Popover_Card_CiteAs.CopyInteraction
                            onClick={onCiteButtonClick}
                            pressed={citeActive}
                            text={trans.copyCitation}
                            copiedText={trans.copied}
                          />
                        }
                      >
                        {citation}
                      </Dc_Popover_Card_CiteAs>
                    </Dc_MetaIntro_ActionStack.CitePopover>
                  </SfcPopover.Content>
                </SfcPopover.Root>
              )}
            </>
          }
          authorCardSlot={
            authors.length > 0 && (
              <>
                {authors.map((author, idx) => (
                  <AuthorCard
                    key={`${author.name}-${idx}`}
                    authorName={author.name}
                    position={author.position}
                    medium={author.image}
                    profileUrl={author.profileUrl}
                    isMarginal={false}
                    children={{ type: "doc", content: [] }}
                  />
                ))}
              </>
            )
          }
          shareSlot={<ShareButton size="large" />}
        >
          {contact && (
            <Dc_MetaIntro_DrawerContent.AccordionStack
              defaultOpen={false}
              head={trans.contact}
              chevron={<Accordion.Chevron />}
            >
              <Dc_MetaIntro_DrawerContent.AccordionItem
                text={contact.name}
                linkSlot={
                  contact.email && (
                    <a href={`mailto:${contact.email}`}>
                      <Dc_MetaIntro_DrawerContent.BasicLink>
                        {contact.email}
                      </Dc_MetaIntro_DrawerContent.BasicLink>
                    </a>
                  )
                }
              ></Dc_MetaIntro_DrawerContent.AccordionItem>
            </Dc_MetaIntro_DrawerContent.AccordionStack>
          )}
          {copyrightNote && (
            <Dc_MetaIntro_DrawerContent.AccordionStack
              defaultOpen={false}
              head={trans.copyright}
              chevron={<Accordion.Chevron />}
            >
              <Dc_MetaIntro_DrawerContent.AccordionItem
                text={copyrightNote.content}
                linkSlot={
                  copyrightNote.link?.url &&
                  copyrightNote.link?.label && (
                    <a href={copyrightNote.link.url}>
                      <Dc_MetaIntro_DrawerContent.BasicLink>
                        {copyrightNote.link.label}
                      </Dc_MetaIntro_DrawerContent.BasicLink>
                    </a>
                  )
                }
              ></Dc_MetaIntro_DrawerContent.AccordionItem>
            </Dc_MetaIntro_DrawerContent.AccordionStack>
          )}
          {imageCredits.length > 0 && (
            <Dc_MetaIntro_DrawerContent.AccordionStack
              defaultOpen={false}
              head={trans.imageCredits}
              chevron={<Accordion.Chevron />}
            >
              {imageCredits.map((credit) => (
                <Dc_MetaIntro_DrawerContent.AccordionItem
                  text={credit.content}
                />
              ))}
            </Dc_MetaIntro_DrawerContent.AccordionStack>
          )}
          {allCitations.length > 0 && (
            <Dc_MetaIntro_DrawerContent.AccordionStack
              defaultOpen={false}
              head={trans.references}
              chevron={<Accordion.Chevron />}
            >
              <ol className="w-full list list-none">
                {allCitations.map((citation, idx) => (
                  <li
                    key={`${citation.index}-${idx}`}
                    className="mb-4 flex flex-row gap-2"
                  >
                    <span className="text-caption font-caption text-subtext-color p-[4px] m-[-4px] group-hover/f2dc83d7:text-compcolortextcitationtriggerhover">
                      [{citation.index}]
                    </span>
                    <Dc_Text_Citation
                      linkSlot={
                        citation.source && (
                          <CitationSourceLink citation={citation} />
                        )
                      }
                    >
                      <Dc_Text_Citation.Text>
                        {citation.content}
                      </Dc_Text_Citation.Text>
                    </Dc_Text_Citation>
                  </li>
                ))}
              </ol>
            </Dc_MetaIntro_DrawerContent.AccordionStack>
          )}
        </Dc_MetaIntro_DrawerContent>
      </Drawer>
    </>
  );
};

export default MetaIntro;
